---
title: "What candidates see when they apply"
description: "The Chosen apply flow from the candidate's side: submitting an application, passwordless magic-link sign-in, and the dashboard for checking status."
category: "careers"
keywords:
  - "candidate apply experience"
  - "application status check"
  - "passwordless sign in"
  - "candidate dashboard"
schema: "HowTo"
nav:
  section: "guides"
  order: 40
  parent: "/guides/careers"
---

A candidate opens a job, reads it, and applies in a few steps. Later they can come back, sign in without a password, and see the status of everything they've applied to. This page walks that flow from their seat — useful for knowing what your applicants actually experience, and for answering the question every recruiter gets.

## Applying to a job

On a published job's page, the candidate sees the description, the role details, and an **Overview** and an **Application** tab. From either one, they reach the apply form.

<Steps>
  <li>They open the **Application** tab, or click the apply button on the overview.</li>
  <li>They enter their name and email and upload a resume.</li>
  <li>They answer any [custom questions](/guides/careers/application-questions) you've added.</li>
  <li>They submit. The application lands in your [candidates table](/guides/candidates).</li>
</Steps>

The whole flow is mobile-friendly — a candidate can do it from a phone on the train. If your form is short, that takes about two minutes.

If someone has applied to your org before with the same email, Chosen recognizes them and pre-fills what it already knows, so a repeat applicant isn't retyping their details.

## Signing in — no password

Candidates never set a password. To come back, they enter their email and Chosen sends a **magic link**. Clicking it signs them in.

The link is single-use and valid only briefly. Used or expired, it does nothing — they just request a fresh one. There's no password to forget, reset, or leak, which means there's nothing for you to support.

## The returning-candidate dashboard

Once signed in, a candidate has a dashboard. It shows:

- The resume on file, which they can replace.
- Answers to a set of profile questions, which they can edit.
- A profile, with an option to be discoverable to other companies on Chosen — theirs to toggle on or off.

Each candidate also gets an auto-generated public profile handle. The public profile is off until the candidate turns it on, and it's their choice, not yours.

<Callout type="tip" title="When a candidate emails asking for their status">
Every recruiter gets this email. The answer: the candidate can check it themselves. They sign in with a magic link and see the jobs they've applied to and where each one stands. Point them there once and the question stops coming back.
</Callout>

## What the candidate controls

The candidate side is genuinely the candidate's. They own their resume, their profile, their answers, and whether their public profile is visible. You can't edit their dashboard or flip their public profile on for them.

What you control is on your side: the job, the form, and how you move them through your [pipeline statuses](/guides/candidates/statuses-and-pipeline).

## Limits

The dashboard shows a candidate which jobs they applied to and the status of each. It is not a messaging channel — there's no in-app chat between you and an applicant. Substantive back-and-forth still happens over email. The dashboard answers "where do I stand?", and that alone removes a lot of status-check email from your inbox.
